import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useState, type PropsWithChildren } from 'react';
import { onUnauthorized } from '../api/client';
import { storageApi } from '../api/storage';
import type { User } from '../domain/models';
import { AuthContext, type AuthValue } from './auth-context';

export function AuthProvider({ children }: PropsWithChildren) {
  const queryClient = useQueryClient();
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(() => Boolean(localStorage.getItem('cloud_token')));
  const clearPrivateData = useCallback(() => {
    void queryClient.cancelQueries();
    queryClient.clear();
  }, [queryClient]);
  const logout = useCallback(() => {
    localStorage.removeItem('cloud_token');
    clearPrivateData();
    setUser(null);
  }, [clearPrivateData]);

  useEffect(() => onUnauthorized(logout), [logout]);
  useEffect(() => {
    if (!localStorage.getItem('cloud_token')) return;
    storageApi.me().then(({ user: current }) => setUser(current)).catch(logout).finally(() => setLoading(false));
  }, [logout]);

  const authenticate = useCallback(async (action: Promise<{ token: string; user: User }>) => {
    const result = await action;
    clearPrivateData();
    localStorage.setItem('cloud_token', result.token);
    setUser(result.user);
  }, [clearPrivateData]);
  const value = useMemo<AuthValue>(() => ({
    user,
    loading,
    login: (email, password) => authenticate(storageApi.login(email, password)),
    register: (name, email, password) => authenticate(storageApi.register(name, email, password)),
    loginWithGoogle: (code, password) => authenticate(storageApi.exchangeGoogle(code, password)),
    logout,
  }), [user, loading, logout, authenticate]);
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
